import { normalizeReviewTarget } from './review-stats';
import type { AppState, PersistedState } from './types';

const DATABASE_NAME = 'paper-bidding';
const DATABASE_VERSION = 1;
const STORE_NAME = 'app-state';
const STATE_KEY = 'current';

export class PersistenceError extends Error {
  readonly operation: 'open' | 'load' | 'save' | 'clear';

  constructor(operation: PersistenceError['operation'], message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'PersistenceError';
    this.operation = operation;
  }
}

function consentError(operation: PersistenceError['operation']): PersistenceError {
  return new PersistenceError(operation, 'Persistence consent is required before accessing saved data.');
}

function requestResult<T>(
  request: IDBRequest<T>,
  operation: PersistenceError['operation'],
): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener(
      'error',
      () =>
        reject(
          new PersistenceError(operation, `IndexedDB ${operation} request failed.`, {
            cause: request.error,
          }),
        ),
      { once: true },
    );
  });
}

function openDatabase(operation: PersistenceError['operation']): Promise<IDBDatabase> {
  if (!globalThis.indexedDB) {
    return Promise.reject(
      new PersistenceError(operation, 'IndexedDB is unavailable in this browser.'),
    );
  }

  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = globalThis.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    } catch (error) {
      reject(new PersistenceError('open', 'Could not open IndexedDB.', { cause: error }));
      return;
    }

    request.addEventListener(
      'upgradeneeded',
      () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME);
        }
      },
      { once: true },
    );
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener(
      'blocked',
      () => reject(new PersistenceError('open', 'IndexedDB upgrade was blocked by another tab.')),
      { once: true },
    );
    request.addEventListener(
      'error',
      () =>
        reject(
          new PersistenceError('open', 'Could not open IndexedDB.', { cause: request.error }),
        ),
      { once: true },
    );
  });
}

interface PersistedStateShape {
  version: unknown;
  profileItems: PersistedState['profileItems'];
  papers: PersistedState['papers'];
  rankings: PersistedState['rankings'];
  profileEmbeddings?: unknown;
  profileBodyEmbeddings?: unknown;
  paperEmbeddings?: unknown;
  negativeKeywordEmbeddings?: unknown;
  decisions: PersistedState['decisions'];
  preferenceMapping: PersistedState['preferenceMapping'];
  negativeKeywords?: unknown;
  exportLimit?: unknown;
  selectedPublicationIds?: unknown;
  similarityLimit?: unknown;
  reviewTarget?: unknown;
  savedAt: string;
}

function isPersistedStateShape(value: unknown): value is PersistedStateShape {
  if (typeof value !== 'object' || value === null) return false;
  const state = value as Partial<PersistedStateShape>;
  return (
    (state.version === 1 ||
      state.version === 2 ||
      state.version === 3 ||
      state.version === 4 ||
      state.version === 5 ||
      state.version === 6) &&
    Array.isArray(state.profileItems) &&
    Array.isArray(state.papers) &&
    Array.isArray(state.rankings) &&
    typeof state.decisions === 'object' &&
    state.decisions !== null &&
    typeof state.preferenceMapping === 'object' &&
    state.preferenceMapping !== null &&
    typeof state.savedAt === 'string'
  );
}

function normalizePersistedState(state: PersistedStateShape): PersistedState {
  const hasEmbeddingState =
    state.version === 4 || state.version === 5 || state.version === 6;
  const negativeKeywords = hasEmbeddingState && Array.isArray(state.negativeKeywords)
    ? state.negativeKeywords.filter((keyword): keyword is string => typeof keyword === 'string')
    : [];
  const paperEmbeddings =
    hasEmbeddingState &&
    typeof state.paperEmbeddings === 'object' &&
    state.paperEmbeddings !== null
      ? (state.paperEmbeddings as Record<string, number[]>)
      : {};
  const storedNegativeKeywordEmbeddings =
    hasEmbeddingState &&
    typeof state.negativeKeywordEmbeddings === 'object' &&
    state.negativeKeywordEmbeddings !== null
      ? (state.negativeKeywordEmbeddings as Record<string, number[]>)
      : {};
  const negativeKeywordEmbeddings = Object.fromEntries(
    negativeKeywords.flatMap((keyword) => {
      const embedding = storedNegativeKeywordEmbeddings[keyword];
      return embedding ? [[keyword, embedding]] : [];
    }),
  );
  const profileEmbeddings =
    (state.version === 5 || state.version === 6) &&
    typeof state.profileEmbeddings === 'object' &&
    state.profileEmbeddings !== null
      ? (state.profileEmbeddings as Record<string, number[]>)
      : {};
  const profileBodyEmbeddings =
    state.version === 6 &&
    typeof state.profileBodyEmbeddings === 'object' &&
    state.profileBodyEmbeddings !== null
      ? (state.profileBodyEmbeddings as PersistedState['profileBodyEmbeddings'])
      : {};
  const exportLimit =
    typeof state.exportLimit === 'number' &&
    Number.isInteger(state.exportLimit) &&
    state.exportLimit > 0
      ? state.exportLimit
      : null;
  const publicationIds = new Set(
    state.profileItems.filter((item) => item.kind === 'publication').map((item) => item.id),
  );
  const selectedPublicationIds =
    (state.version === 5 || state.version === 6) && Array.isArray(state.selectedPublicationIds)
      ? state.selectedPublicationIds.filter(
          (id): id is string => typeof id === 'string' && publicationIds.has(id),
        )
      : [];
  const similarityLimit =
    (state.version === 5 || state.version === 6) &&
    typeof state.similarityLimit === 'number' &&
    Number.isInteger(state.similarityLimit) &&
    state.similarityLimit >= 1 &&
    state.similarityLimit <= 100
      ? state.similarityLimit
      : 20;
  const reviewTarget = normalizeReviewTarget(state.reviewTarget);
  return {
    version: 6,
    profileItems: state.profileItems,
    papers: state.papers,
    rankings: state.rankings,
    profileEmbeddings,
    profileBodyEmbeddings,
    paperEmbeddings,
    negativeKeywordEmbeddings,
    decisions: state.decisions,
    preferenceMapping: state.preferenceMapping,
    negativeKeywords,
    exportLimit,
    selectedPublicationIds,
    similarityLimit,
    reviewTarget,
    savedAt: state.savedAt,
  };
}

function persistedStateSnapshot(state: AppState | PersistedState): PersistedState {
  return {
    version: 6,
    profileItems: state.profileItems,
    papers: state.papers,
    rankings: state.rankings,
    profileEmbeddings: state.profileEmbeddings,
    profileBodyEmbeddings: state.profileBodyEmbeddings,
    paperEmbeddings: state.paperEmbeddings,
    negativeKeywordEmbeddings: state.negativeKeywordEmbeddings,
    decisions: state.decisions,
    preferenceMapping: state.preferenceMapping,
    negativeKeywords: state.negativeKeywords,
    exportLimit: state.exportLimit,
    selectedPublicationIds: state.selectedPublicationIds,
    similarityLimit: state.similarityLimit,
    reviewTarget: state.reviewTarget,
    savedAt: new Date().toISOString(),
  };
}

/** Creates a portable JSON backup of the current reviewing session. */
export function exportPersistedState(state: AppState | PersistedState): string {
  return `${JSON.stringify(persistedStateSnapshot(state), null, 2)}\n`;
}

/** Parses and normalizes a portable reviewing-session backup. */
export function importPersistedState(source: string): PersistedState {
  let value: unknown;
  try {
    value = JSON.parse(source);
  } catch (error) {
    throw new PersistenceError('load', 'Reviewing preferences file is not valid JSON.', {
      cause: error,
    });
  }
  if (!isPersistedStateShape(value)) {
    throw new PersistenceError(
      'load',
      'Reviewing preferences file is invalid or uses an unsupported version.',
    );
  }
  return normalizePersistedState(value);
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: 'load' | 'save' | 'clear',
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  let database: IDBDatabase;
  try {
    database = await openDatabase(operation);
  } catch (error) {
    if (error instanceof PersistenceError) throw error;
    throw new PersistenceError(operation, `IndexedDB ${operation} failed.`, { cause: error });
  }

  try {
    const transaction = database.transaction(STORE_NAME, mode);
    const transactionCompletion = new Promise<void>((resolve, reject) => {
      transaction.addEventListener('complete', () => resolve(), { once: true });
      transaction.addEventListener(
        'abort',
        () =>
          reject(
            new PersistenceError(operation, `IndexedDB ${operation} transaction was aborted.`, {
              cause: transaction.error,
            }),
          ),
        { once: true },
      );
      transaction.addEventListener(
        'error',
        () =>
          reject(
            new PersistenceError(operation, `IndexedDB ${operation} transaction failed.`, {
              cause: transaction.error,
            }),
          ),
        { once: true },
      );
    });
    const [result] = await Promise.all([
      requestResult(action(transaction.objectStore(STORE_NAME)), operation),
      transactionCompletion,
    ]);
    return result;
  } catch (error) {
    if (error instanceof PersistenceError) throw error;
    throw new PersistenceError(operation, `IndexedDB ${operation} failed.`, { cause: error });
  } finally {
    database.close();
  }
}

/**
 * Loads the sole saved state. Passing false does not open IndexedDB.
 */
export async function loadPersistedState(consentGranted: boolean): Promise<PersistedState | null> {
  if (!consentGranted) throw consentError('load');
  const value: unknown = await withStore('readonly', 'load', (store) => store.get(STATE_KEY));
  if (value === undefined) return null;
  if (!isPersistedStateShape(value)) {
    throw new PersistenceError('load', 'Saved state is invalid or uses an unsupported version.');
  }
  return normalizePersistedState(value);
}

/**
 * Saves one versioned snapshot. Passing false does not open or write IndexedDB.
 */
export async function savePersistedState(
  state: AppState | PersistedState,
  consentGranted: boolean,
): Promise<PersistedState> {
  if (!consentGranted) throw consentError('save');
  const persisted = persistedStateSnapshot(state);
  await withStore('readwrite', 'save', (store) => store.put(persisted, STATE_KEY));
  return persisted;
}

/**
 * Clears the sole saved state. Passing false does not open or write IndexedDB.
 */
export async function clearPersistedState(consentGranted: boolean): Promise<void> {
  if (!consentGranted) throw consentError('clear');
  await withStore('readwrite', 'clear', (store) => store.delete(STATE_KEY));
}

export const loadState = loadPersistedState;
export const saveState = savePersistedState;
export const clearState = clearPersistedState;
